import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Loader2, X } from 'lucide-react';
import { listConversations, ConversationSummary } from '../../services/api';
import './ConversationList.css';

interface ConversationListProps {
  isOpen: boolean;
  onClose: () => void;
  activeConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  isOpen,
  onClose,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
}) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen]);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await listConversations();
      setConversations(data || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleSelectConversation = (conversationId: string) => {
    onSelectConversation(conversationId);
    onClose();
  };

  const handleNewConversation = () => {
    onNewConversation();
    onClose();
  };

  return (
    <div className={`conversation-list ${isOpen ? 'open' : ''}`}>
      <div className="conversation-list-header">
        <h3>Conversations</h3>
        <div className="conversation-header-actions">
          <button
            className="conversation-action-btn new-conversation-btn"
            onClick={handleNewConversation}
            title="New Conversation"
          >
            <Plus size={20} />
          </button>
          <button
            className="conversation-action-btn close-conversation-btn"
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="conversation-list-loading">
          <Loader2 size={24} className="spinner" />
          <p>Loading conversations...</p>
        </div>
      ) : error ? (
        <div className="conversation-list-error">
          <p>{error}</p>
          <button onClick={loadConversations} className="retry-btn">
            Retry
          </button>
        </div>
      ) : !conversations || conversations.length === 0 ? (
        <div className="conversation-list-empty">
          <MessageSquare size={48} />
          <p>No conversations yet</p>
          <p className="empty-subtitle">Start chatting to create your first conversation</p>
        </div>
      ) : (
        <div className="conversation-list-items">
          {conversations.map((conv) => (
            <button
              key={conv.conversationId}
              className={`conversation-item ${
                activeConversationId === conv.conversationId ? 'active' : ''
              }`}
              onClick={() => handleSelectConversation(conv.conversationId)}
            >
              <div className="conversation-item-icon">
                <MessageSquare size={20} />
              </div>
              <div className="conversation-item-content">
                <div className="conversation-item-preview">
                  {truncateText(conv.lastMessage)}
                </div>
                <div className="conversation-item-meta">
                  <span className="conversation-item-count">
                    {conv.messageCount} {conv.messageCount === 1 ? 'message' : 'messages'}
                  </span>
                  <span className="conversation-item-date">
                    {formatDate(conv.lastTimestamp)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationList;

